import { useEffect, useState } from "react";
import { LanguageOption, LanguageTitleOptions } from "../types";
import { useTranslation } from "react-i18next";
import { SiteLocales } from "../../../../../../common/enums";
import { ReactComponent as UKFlag } from "../assets/uk.svg";
import { ReactComponent as USFlag } from "../assets/usa.svg";
import { ReactComponent as FRFlag } from "../assets/france.svg";
import { ReactComponent as JPFlag } from "../assets/japan.svg";
import { ReactComponent as AUFlag } from "../assets/austria.svg";
import { ReactComponent as DEFlag } from "../assets/germany.svg";
import { ReactComponent as BRPTFlag } from "../assets/brazil.svg";
import { ReactComponent as MXFlag } from "../assets/mexico.svg";
import { UI_LANG_KEY } from "../../../../../../common/constants";

export const useLanguageSwitch = () => {
  const { t, i18n } = useTranslation("common");
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const [supportedLanguages, setSupportedLanguages] = useState<Array<LanguageOption>>([]);
  const [selectedLanguage, setSelectedLanguage] = useState({
    title: t("header.languages.englishUK"),
    code: SiteLocales.EN_GB
  });

  const handleAccordionChange = () => {
    setAccordionExpanded(!accordionExpanded);
  };

  const onLanguageChange = async (newLanguage: SiteLocales) => {
    const languages = getSupportedLanguages({ hideDefaultTitle: true });
    setSelectedLanguage(languages.find(lang => lang.code === newLanguage)!);
    await i18n.changeLanguage(newLanguage);
    localStorage.setItem(UI_LANG_KEY, newLanguage);
    handleAccordionChange();
    setSupportedLanguages(getSupportedLanguages());
  };

  const getLocaleFromCache = () => {
    const languages = getSupportedLanguages({ hideDefaultTitle: true });
    const savedLocale = localStorage.getItem(UI_LANG_KEY);
    if (savedLocale) {
      const locale = languages.find(lang => lang.code === savedLocale);
      if (locale) {
        return locale;
      }
    }
    return languages.find(lang => lang.code === SiteLocales.EN_GB)!;
  };

  const configureInitialLocale = () => {
    const locale = getLocaleFromCache();
    setSelectedLanguage(locale);
    i18n.changeLanguage(locale.code);
    configureSupportedLanguages();
  };

  const getFlagByLanguage = (language: SiteLocales) => {
    if (language === SiteLocales.EN_US) {
      return <USFlag />;
    }
    if (language === SiteLocales.FR_FR) {
      return <FRFlag />;
    }
    if (language === SiteLocales.JA_JP) {
      return <JPFlag />;
    }
    if (language === SiteLocales.DE_AT) {
      return <AUFlag />;
    }
    if (language === SiteLocales.DE_DE) {
      return <DEFlag />;
    }
    if (language === SiteLocales.PT_BR) {
      return <BRPTFlag />;
    }
    if (language === SiteLocales.ES_MX) {
      return <MXFlag />;
    }
    return <UKFlag />;
  };

  const getSupportedLanguages = (options?: LanguageTitleOptions) => {
    let enGbTitle = `${t("header.languages.englishUK")} - ${t("header.languages.default")}`;

    if (options?.hideDefaultTitle) {
      enGbTitle = t("header.languages.englishUK");
    }

    const enGBLocale = { code: SiteLocales.EN_GB, title: enGbTitle };
    const enUSLocale = { code: SiteLocales.EN_US, title: t("header.languages.englishUS") };
    const frFRLocale = { code: SiteLocales.FR_FR, title: t("header.languages.french") };
    const jaJPLocale = { code: SiteLocales.JA_JP, title: t("header.languages.japanese") };
    const deDELocale = { code: SiteLocales.DE_DE, title: t("header.languages.germanDE") };
    const ptBRLocale = { code: SiteLocales.PT_BR, title: t("header.languages.brazilianPortuguese") };
    const esMXLocale = { code: SiteLocales.ES_MX, title: t("header.languages.spanishMX") };
    return [enGBLocale, enUSLocale, frFRLocale, deDELocale, jaJPLocale, ptBRLocale, esMXLocale];
  };

  const configureSupportedLanguages = () => {
    setSupportedLanguages(getSupportedLanguages());
  };

  useEffect(() => {
    configureInitialLocale();
  }, []);

  return {
    supportedLanguages,
    selectedLanguage,
    onLanguageChange,
    getFlagByLanguage,
    accordionExpanded,
    handleAccordionChange,
    configureInitialLocale
  };
};
